import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { ExternalLink, Plus, RefreshCw, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Modal } from '../components/Modal'
import { StatusPill } from '../components/StatusPill'
import { api } from '../lib/api'

type TagModal = 'batch' | 'replace'

export function TagsLive() {
  const [tags, setTags] = useState<any[]>([])
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('')
  const [modal, setModal] = useState<TagModal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modalError, setModalError] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    setLoading(true)
    try {
      setTags(await api.tags())
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void reload() }, [])

  const filtered = useMemo(() => tags.filter((tag) => (
    (!query || `${tag.public_code} ${tag.batch_code}`.toLowerCase().includes(query.toLowerCase())) &&
    (!status || tag.status === status)
  )), [tags, query, status])
  const batches = useMemo(() => new Set(tags.map((tag) => tag.batch_code).filter(Boolean)).size, [tags])
  const inventory = useMemo(() => tags.filter((tag) => tag.status === 'inventory').length, [tags])
  const activated = useMemo(() => tags.filter((tag) => tag.status === 'activated').length, [tags])

  async function createBatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setModalError('')
    const form = new FormData(event.currentTarget)
    try {
      await api.createTagBatch({
        batchCode: form.get('batchCode'),
        quantity: Number(form.get('quantity')),
        prefix: form.get('prefix') || undefined,
        material: form.get('material'),
        status: 'inventory',
      })
      setModal(null)
      await reload()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function replaceTag(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setModalError('')
    const form = new FormData(event.currentTarget)
    try {
      await api.replaceTag({
        oldCode: form.get('oldCode'),
        newCode: form.get('newCode'),
        reason: form.get('reason') || 'damaged_tag',
      })
      setModal(null)
      await reload()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const modalLabel = modal === 'batch' ? 'Generate tag batch' : 'Replace tag'

  return (
    <div className="page-stack">
      <section className="page-header-row">
        <div>
          <h2>Tag inventory</h2>
          <p>Replace a damaged label while preserving the tree record.</p>
        </div>
        <div className="hero-actions">
          <button className="button button--primary" onClick={() => { setModalError(''); setModal('batch') }}><Plus size={17} /> Generate batch</button>
          <button className="button button--secondary" onClick={() => { setModalError(''); setModal('replace') }}><RefreshCw size={17} /> Replace tag</button>
          <button className="button button--secondary" onClick={() => void reload()} disabled={loading}><RefreshCw size={16} /> Refresh</button>
        </div>
      </section>

      {error && <section className="panel" role="alert"><div className="panel-heading"><div><h3>Could not load tag inventory</h3></div><button className="button button--secondary" onClick={() => void reload()} disabled={loading}>Try again</button></div><p style={{ padding: '0 19px 17px', margin: 0 }}>{error}</p></section>}

      <section className="tag-stat-grid" aria-label="Tag inventory totals">
        <div className="compact-stat"><span>Total tags<strong>{loading || error ? '—' : tags.length}</strong></span></div>
        <div className="compact-stat"><span>Batches<strong>{loading || error ? '—' : batches}</strong></span></div>
        <div className="compact-stat"><span>In inventory<strong>{loading || error ? '—' : inventory}</strong></span></div>
        <div className="compact-stat"><span>Activated<strong>{loading || error ? '—' : activated}</strong></span></div>
      </section>

      <section className="panel">
        <div className="panel-heading"><div><h3>{loading ? 'Loading tags…' : error ? '—' : `${filtered.length} matching tags`}</h3></div></div>
        <div className="toolbar-card" style={{ border: 0, borderRadius: 0, boxShadow: 'none' }}>
          <label className="search-field"><Search size={16} /><input aria-label="Search tag code or batch" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search code or batch" /></label>
          <label className="field-label"><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option><option value="inventory">Inventory</option><option value="issued">Issued</option><option value="activated">Activated</option><option value="retired">Retired</option></select></label>
        </div>
        {loading ? <div className="empty-state" role="status"><span>Loading tag inventory…</span></div> : filtered.length === 0 ? <div className="empty-state"><strong>No matching tags</strong><span>Try a different code, batch or status.</span></div> : <div className="campaign-list">{filtered.slice(0, 300).map((tag) => <article className="campaign-row" key={tag.id}><div className="campaign-row__heading"><div><strong>{tag.public_code}</strong><span>{tag.batch_code} · {tag.material || 'Material unspecified'}{tag.tree_organism_id ? ' · linked tree' : ' · unassigned'}</span></div><div className="hero-actions">
          {tag.tree_organism_id && <Link className="button button--secondary button--small" to={`/t/${encodeURIComponent(tag.public_code)}`}><ExternalLink size={14} /> Passport</Link>}
          <a className="button button--secondary button--small" href={`/api/tags/${encodeURIComponent(tag.public_code)}/qr.svg`} target="_blank" rel="noreferrer"><ExternalLink size={14} /> QR</a>
          <StatusPill value={tag.status} compact />
        </div></div></article>)}</div>}
        {!loading && filtered.length > 300 && <p style={{ padding: '0 19px 17px', margin: 0, color: 'var(--muted)' }}>Showing the first 300 matching tags.</p>}
      </section>

      {modal && <Modal label={modalLabel} onClose={() => { if (!busy) setModal(null) }}>
        <h2>{modalLabel}</h2>
        {modalError && <div className="inline-alert inline-alert--danger" role="alert"><div><strong>Could not save this change</strong><span>{modalError}</span></div></div>}
        {modal === 'batch' ? <form onSubmit={createBatch}>
          <label className="field-label"><span>Batch code</span><input name="batchCode" required placeholder="AUTUMN-26-A" /></label>
          <label className="field-label"><span>Quantity</span><input name="quantity" type="number" min="1" max="10000" defaultValue="100" required /></label>
          <label className="field-label"><span>Public code prefix <em>optional</em></span><input name="prefix" maxLength={8} placeholder="TREE26" /></label>
          <label className="field-label"><span>Material</span><select name="material"><option>UV-stable outdoor polymer</option><option>Anodized aluminium</option><option>Outdoor laminated composite</option></select></label>
          <button disabled={busy} className="button button--primary button--wide">{busy ? 'Generating…' : 'Generate inventory'}</button>
        </form> : <form onSubmit={replaceTag}>
          <label className="field-label"><span>Current active tag</span><input name="oldCode" required placeholder="TREE26-ABC12345" /></label>
          <label className="field-label"><span>Unused replacement tag</span><input name="newCode" required placeholder="TREE26-XYZ67890" /></label>
          <label className="field-label"><span>Reason</span><select name="reason"><option value="damaged_tag">Damaged / unreadable</option><option value="missing_tag">Missing</option><option value="mounting_replacement">Mounting replacement</option><option value="administrative_correction">Administrative correction</option></select></label>
          <button disabled={busy} className="button button--primary button--wide">{busy ? 'Replacing…' : 'Transfer tree to replacement tag'}</button>
        </form>}
        <button type="button" className="button button--secondary button--wide" onClick={() => setModal(null)} disabled={busy}>Cancel</button>
      </Modal>}
    </div>
  )
}
